<template>
  <div class="ui-page">
    <app-filter-toolbar>
      <template #primary><AppInput id="refund-search" v-model="query" design="new" type="search" placeholder="Reference or client" aria-label="Search" @input="scheduleSearch" @keydown.enter.native.prevent="search" /></template>
      <AppSelect v-model="manager" design="new" input-id="refund-manager" aria-label="Responsible manager" :options="managerOptions" label="full_name" placeholder="Responsible manager" :filterable="false" @search="searchManagers" @open="loadManagers" @input="search" />
      <AppDatePicker :key="String(range)" v-model="dates" design="new" :range="range" :input-attr="{ id: 'refund-created', 'aria-label': 'Created on' }" placeholder="Created on" @input="search" />
      <AppSelect v-if="!queue" v-model="status" design="new" input-id="refund-status" aria-label="Refund status" :options="states" :reduce="item => item.code" label="name" placeholder="Refund status" @input="search" />
      <template #selection><app-filters design="new" refined weight="medium" name="refund-date-mode" aria-label="Date filter mode" :value="range ? 'range' : 'day'" :filters="dateModeOptions" @change="setRange($event === 'range')" /></template>
      <template v-if="hasFilters" #clear><AppClearFiltersButton design="new" icon-only @clear="clear" /></template>
    </app-filter-toolbar>
    <AppErrorState v-if="managerError" :message="managerError" retry-label="Retry managers" @retry="loadManagers" />
    <AppContentSkeleton v-if="loading" label="Loading refunds" :columns="columns" min-width="1200px" />
    <AppErrorState v-else-if="error" :message="error" @retry="load" />
    <template v-else>
      <AppCard v-if="rows.length" design="new" variant="table">
        <AppDataTable design="new" density="dense" :columns="columns" :rows="rows" min-width="1200px" clickable @row-click="openRow">
          <template #cell-client_name="{ row }"><AppTableText :text="row.client_name" /></template>
          <template #cell-reference="{ row }"><AppTableText :text="row.reference" /></template>
          <template #cell-amount="{ row }">{{ money(row.amount, row.currency) }}</template>
          <template #cell-paid_amount="{ row }">{{ money(row.paid_amount, row.currency) }}</template>
          <template #cell-verified_amount="{ row }">{{ money(row.verified_amount, row.currency) }}</template>
          <template #cell-created_at="{ row }"><AppTableText :text="dateLabel(row.created_at)" /></template>
          <template #cell-paid_at="{ row }"><AppTableText :text="dateLabel(row.paid_at)" /></template>
          <template #cell-status="{ row }"><AppStatusBadge design="new" compact :label="refundState(row.status).name" :tone="refundState(row.status).tone" /></template>
          <template #cell-manager_name="{ row }"><AppTableUser :name="row.manager_name || ''" /></template>
        </AppDataTable>
      </AppCard>
      <app-no-results v-else design="new" title="No refunds found" text="" />
      <AppPagination design="new" placement="embedded" :page="page" :page-size="25" :count="count" :visible-count="rows.length" :loading="loading" @change="changePage" />
    </template>
    <DetailModal :selection="selection" @close="close" @updated="load" />
  </div>
</template>
<script>
import AppCard from '@/components/AppCard'
import AppDataTable from '@/components/AppDataTable'
import AppPagination from '@/components/AppPagination'
import AppInput from '@/components/AppInput'
import AppSelect from '@/components/AppSelect'
import AppStatusBadge from '@/components/AppStatusBadge'
import AppDatePicker from '@/components/AppDatePicker'
import AppClearFiltersButton from '@/components/AppClearFiltersButton'
import AppContentSkeleton from '@/components/AppContentSkeleton'
import AppErrorState from '@/components/AppErrorState'
import AppTableText from '@/components/AppTableText'
import AppTableUser from '@/components/AppTableUser'
import DetailModal from './DetailModal'
import { getFinanceRefunds, getFinanceRefundPayouts, getUsers } from '@/http'
import { money, refundState, refundStates, errorMessage } from '@/views/safari/cancellations/format'
import { dateLabel } from './format'
export default {
  name: 'FinanceRefundList',
  components: { AppCard, AppDataTable, AppPagination, AppInput, AppSelect, AppStatusBadge, AppDatePicker, AppClearFiltersButton, AppContentSkeleton, AppErrorState, AppTableText, AppTableUser, DetailModal },
  props: { queue: { type: String, default: '' } },
  data() { return { rows: [], count: 0, page: 1, query: '', status: null, range: true, dates: [], manager: null, managers: [], managerQuery: '', managerError: '', managerRequest: 0, managerTimer: null, searchTimer: null, loading: false, error: '', requestId: 0, states: refundStates, opener: null } },
  computed: {
    dateModeOptions() { return [{ value: 'day', label: 'Day' }, { value: 'range', label: 'Range' }] },
    dateBounds() { return this.range ? this.dates || [] : [this.dates, this.dates] },
    hasFilters() { return Boolean(this.query || this.status || this.manager || this.dateBounds.some(Boolean)) },
    managerOptions() {
      const options = [...this.managers, ...this.rows.filter(row => row.manager_id).map(row => ({ id: row.manager_id, full_name: row.manager_name })), ...(this.manager ? [this.manager] : [])]
      return options.filter((item, index) => options.findIndex(option => option.id === item.id) === index && (item.full_name || '').toLowerCase().includes(this.managerQuery.toLowerCase()))
    },
    selection() {
      const kind = this.$route.query.payout ? 'payout' : 'refund'
      const value = this.$route.query[kind]
      return /^\d+$/.test(String(value || '')) ? { kind, id: Number(value) } : null
    },
    columns() {
      return [
        { key: 'client_name', label: 'Client' }, { key: 'reference', label: 'Reference' }, { key: 'created_at', label: 'Request created' },
        ...(this.queue ? [{ key: 'amount', label: this.queue === 'completed' ? 'Refund paid' : 'To pay' }] : [{ key: 'paid_amount', label: 'Refundable total' }, { key: 'verified_amount', label: 'Refund paid' }]),
        ...(this.queue === 'completed' ? [{ key: 'paid_at', label: 'Paid on' }] : []),
        { key: 'manager_name', label: 'Responsible manager' }, { key: 'status', label: 'Status', width: '200px' }
      ]
    }
  },
  watch: { queue() { this.clear() } },
  mounted() { this.load() },
  beforeDestroy() { this.requestId += 1; this.managerRequest += 1; clearTimeout(this.searchTimer); clearTimeout(this.managerTimer) },
  methods: {
    money,
    dateLabel,
    refundState,
    async load() {
      const current = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const fn = this.queue ? getFinanceRefundPayouts : getFinanceRefunds
        const { data } = await fn({ state: this.queue ? this.queue === 'completed' ? 'paid' : 'pending' : undefined, search: this.query || undefined, status: this.queue ? undefined : this.status || undefined, manager_id: this.manager ? this.manager.id : undefined, created_from: this.dateBounds[0] || undefined, created_to: this.dateBounds[1] || undefined, page: this.page })
        if (current === this.requestId) {
          this.rows = this.queue ? data.results.map(payout => ({ ...payout.refund, id: payout.id, amount: payout.amount, paid_at: payout.paid_at, status: payout.state === 'paid' ? 'verified' : 'reported' })) : data.results
          this.count = data.count
        }
      } catch (error) { if (current === this.requestId) this.error = errorMessage(error) } finally { if (current === this.requestId) this.loading = false }
    },
    scheduleSearch() { clearTimeout(this.searchTimer); this.requestId += 1; this.searchTimer = setTimeout(this.search, 350) },
    search() { clearTimeout(this.searchTimer); this.page = 1; this.load() },
    setRange(value) { if (this.range === value) return; this.range = value; this.dates = value ? [] : null; this.search() },
    clear() { this.query = ''; this.status = null; this.manager = null; this.managerQuery = ''; this.dates = this.range ? [] : null; this.search() },
    searchManagers(value) { this.managerQuery = value; this.managerRequest += 1; clearTimeout(this.managerTimer); this.managerTimer = setTimeout(this.loadManagers, 250) },
    async loadManagers() {
      const current = ++this.managerRequest
      this.managerError = ''
      try {
        const { data } = await getUsers({ group: this.managerQuery ? undefined : 'sales_manager', search: this.managerQuery, page_size: 30 })
        if (current === this.managerRequest) this.managers = Array.isArray(data) ? data : data.results || []
      } catch (error) { if (current === this.managerRequest) this.managerError = errorMessage(error) }
    },
    changePage(page) { this.page = page; this.load() },
    openRow({ row, event }) {
      this.opener = event.currentTarget
      const query = { ...this.$route.query }
      delete query.refund
      delete query.payout
      query[this.queue ? 'payout' : 'refund'] = String(row.id)
      this.$router.push({ query }).catch(() => {})
    },
    close() {
      if (!this.selection) return
      const query = { ...this.$route.query }
      delete query.refund
      delete query.payout
      this.$router.replace({ query }).catch(() => {})
      this.$nextTick(() => { if (this.opener && this.opener.isConnected) this.opener.focus() })
    }
  }
}
</script>
<style lang="scss">
@import '../../safari/cancellations/cancellations';
</style>
